/**
 * User-facing copy for Smart Campaign (Create Design → Smart Campaign tab).
 * Phrases align with backend brief parsing (voiceover, CTA, music, personalization, formats).
 */

export const SMART_CAMPAIGN_INTRO =
  "Describe what you want to promote — AI plans scenes, layouts, text, music, and effects. Your uploads are used first; without files, stock media may be added. Say “with voiceover on each scene” for narration; actor video and PiP need explicit phrases (see tips).";

export const SMART_CAMPAIGN_MODES_HEADING = "How to start";

export const SMART_CAMPAIGN_COMPOSER_LABEL_DEFAULT = "Campaign brief";

export const SMART_CAMPAIGN_COMPOSER_HINT_DEFAULT =
  "Goal, audience, tone, ~length, aspect ratio (16:9 / 9:16), voiceover, music, colours, CTA with https:// link.";

export const SMART_CAMPAIGN_COMPOSER_LABEL_WITH_ASSETS = "Campaign brief (recommended with assets)";

export const SMART_CAMPAIGN_COMPOSER_HINT_WITH_ASSETS =
  "How to use your files: brochure montage, colours, with voiceover, CTA, personalized actor video, personalized PiP, use only my uploads.";

export const SMART_CAMPAIGN_COMPOSER_HINT_UPLOADS_ONLY =
  "You can generate from files alone, but a brief helps AI sequence scenes, text, and effects.";

export const SMART_CAMPAIGN_REQUIREMENT_HINT =
  "Attach assets, enter a brief, or both to enable Generate.";

/** Order: recommended first, then prompt alone, then assets-only fallback. */
export const SMART_CAMPAIGN_START_MODES = [
  {
    id: "uploads-and-prompt",
    title: "Assets + brief",
    description: "Recommended — your files plus how AI should use them.",
    icon: "fa-layer-group",
    recommended: true,
  },
  {
    id: "prompt-only",
    title: "Brief only",
    description: "No files — AI may add stock photos, video, and background music.",
    icon: "fa-comment-alt",
  },
  {
    id: "uploads-only",
    title: "Assets only",
    description: "Files without a brief — add a prompt for better sequencing.",
    icon: "fa-paperclip",
  },
];

export const SMART_CAMPAIGN_TIPS = [
  "Best results: attach assets and add a brief — say what to promote, audience, tone, length (e.g. ~30 seconds), and aspect ratio (16:9, 9:16, or 1:1).",
  "Brief only (no files): AI may add stock photos, video clips, and background music to fill gaps.",
  "Prefer your uploads: attach enough images/video for every scene. Say “use only my uploads” — stock is skipped when each scene has an assigned file.",
  "PDF brochure montage: upload the PDF and say “brochure montage” or “one panel per scene” with split layout — not every PDF auto-becomes a page-by-page video.",
  "Voiceover: say “with voiceover” or “voiceover on each scene” (not “voiceover throughout”). Audio is generated after the draft is created.",
  "No voiceover: say “no voiceover” or “without narration”.",
  "CTA button: say “end with a Learn More CTA linking to https://…” — you need both the word CTA (or “CTA at the end”) and a real https:// link.",
  "Personalized opening: “opening personalized actor video” or “talking head”.",
  "PiP overlay: “personalized PiP” or “personalized picture-in-picture”.",
  "Music: “upbeat background music”, “cinematic music”, “calm ambient music”, or “no background music”. Upload an audio file to use your own track.",
  "Colours: “dark navy backgrounds, white headlines, gold accents” or hex codes (#001f3f). Say “use account brand colors” to pull tenant branding.",
  "Vertical/social: include “TikTok”, “Instagram reel”, “9:16”, or “vertical” for a portrait video.",
  "Style: cinematic, energetic, corporate, or minimal — AI picks layouts, transitions, and text effects from the editor catalog.",
];

export const SMART_CAMPAIGN_SAMPLE_PROMPTS = [
  {
    id: "pdf-brochure-both",
    label: "PDF brochure",
    forModes: ["uploads-only", "uploads-and-prompt"],
    prompt:
      "Create a ~30 second 16:9 brochure montage from the uploaded PDF. One panel per scene with split layout — headline beside each image. Use only my uploads, no stock. Dark navy backgrounds, white headlines, gold accents. Voiceover on each scene. End with a Learn More CTA linking to https://example.com/learn-more.",
  },
  {
    id: "uploads-styling",
    label: "Assets + style",
    forModes: ["uploads-only", "uploads-and-prompt"],
    prompt:
      "Use only my uploads, no stock. ~30 second 16:9 promo. Forest green backgrounds, yellow headline text, white subtext. Premium tone. With voiceover on each scene.",
  },
  {
    id: "prompt-only-promo",
    label: "Brief only",
    forModes: ["empty", "prompt-only"],
    prompt:
      "Create a premium savings account promo for young professionals. Friendly energetic tone, ~30 seconds, 16:9. With voiceover on each scene. Upbeat background music. End with a Learn More CTA linking to https://example.com/savings.",
  },
  {
    id: "personalized-pip",
    label: "Actor + PiP",
    forModes: ["uploads-only", "uploads-and-prompt"],
    prompt:
      "Use only my uploads, no stock. ~30 second 16:9 promo. Opening personalized actor video. Personalized PiP overlay on the timeline. Dark blue style, white headlines. With voiceover on each scene.",
  },
  {
    id: "vertical-reel",
    label: "Vertical reel",
    forModes: ["empty", "prompt-only", "uploads-and-prompt"],
    prompt:
      "Instagram reel for a summer travel sale. Energetic tone, ~25 seconds, 9:16 vertical. With voiceover on each scene. Upbeat background music. End with a Book Now CTA linking to https://example.com/book.",
  },
  {
    id: "silent-promo",
    label: "No music",
    forModes: ["uploads-only", "uploads-and-prompt", "prompt-only"],
    prompt:
      "Corporate product overview, professional tone, ~30 seconds, 16:9. Use only my uploads. No background music. Minimal text, cinematic style.",
  },
];

export function getSmartCampaignSamplePrompts(mode = "empty") {
  void mode;
  // Show every sample up front — users often pick a prompt before attaching PDFs or assets.
  return SMART_CAMPAIGN_SAMPLE_PROMPTS;
}

export function getSmartCampaignSuggestedMode(activeMode) {
  void activeMode;
  return null;
}

export function matchSmartCampaignSamplePromptId(prompt = "") {
  const normalized = String(prompt || "").trim();
  if (!normalized) {
    return null;
  }
  const matched = SMART_CAMPAIGN_SAMPLE_PROMPTS.find(
    (sample) => sample.prompt.trim() === normalized,
  );
  return matched?.id || null;
}

export function getSmartCampaignModeStatusMessage(activeMode) {
  if (activeMode === "prompt-only") {
    return "Current: brief only — attach assets to prioritize your uploads over stock media.";
  }
  if (activeMode === "uploads-only") {
    return "Current: assets only — add a brief so AI can sequence scenes and effects.";
  }
  if (activeMode === "uploads-and-prompt") {
    return "Current: assets + brief (recommended).";
  }
  return null;
}

export const SMART_CAMPAIGN_PROMPT_PLACEHOLDERS = {
  empty:
    "Attach assets and describe your campaign — goal, tone, colours, with voiceover, CTA with https:// link…",
  "uploads-only":
    "Add a brief: brochure montage, use only my uploads, with voiceover on each scene…",
  "prompt-only":
    "Example: Premium savings promo. ~30s, 16:9, with voiceover on each scene, upbeat music, Learn More CTA with https:// link.",
  "uploads-and-prompt":
    "Example: Brochure montage from PDF. Use only my uploads. With voiceover on each scene, personalized PiP.",
};

/** @deprecated Use SMART_CAMPAIGN_PROMPT_PLACEHOLDERS */
export const SMART_CAMPAIGN_PROMPT_PLACEHOLDER = SMART_CAMPAIGN_PROMPT_PLACEHOLDERS.empty;

/**
 * @param {string} prompt
 * @param {File[]} files
 * @returns {'empty' | 'uploads-only' | 'prompt-only' | 'uploads-and-prompt'}
 */
export function resolveSmartCampaignInputMode(prompt = "", files = []) {
  const hasPrompt = String(prompt || "").trim().length > 0;
  const hasFiles = Array.isArray(files) && files.length > 0;
  if (hasPrompt && hasFiles) {
    return "uploads-and-prompt";
  }
  if (hasFiles) {
    return "uploads-only";
  }
  if (hasPrompt) {
    return "prompt-only";
  }
  return "empty";
}

export function getSmartCampaignPlaceholder(mode) {
  return SMART_CAMPAIGN_PROMPT_PLACEHOLDERS[mode] || SMART_CAMPAIGN_PROMPT_PLACEHOLDERS.empty;
}

export function getSmartCampaignComposerLabel(mode) {
  if (mode === "uploads-only" || mode === "uploads-and-prompt") {
    return SMART_CAMPAIGN_COMPOSER_LABEL_WITH_ASSETS;
  }
  return SMART_CAMPAIGN_COMPOSER_LABEL_DEFAULT;
}

export function getSmartCampaignComposerHint(mode) {
  if (mode === "uploads-and-prompt") {
    return SMART_CAMPAIGN_COMPOSER_HINT_WITH_ASSETS;
  }
  if (mode === "uploads-only") {
    return SMART_CAMPAIGN_COMPOSER_HINT_UPLOADS_ONLY;
  }
  return SMART_CAMPAIGN_COMPOSER_HINT_DEFAULT;
}

export function getSmartCampaignReadyMessage(mode, fileCount = 0) {
  if (mode === "uploads-only") {
    const label = fileCount === 1 ? "1 file" : `${fileCount} files`;
    return `Ready — ${label} attached. Add a brief so AI can sequence your campaign.`;
  }
  if (mode === "prompt-only") {
    return "Ready — brief only. AI may use stock visuals and music; attach files to use your own media.";
  }
  if (mode === "uploads-and-prompt") {
    const label = fileCount === 1 ? "1 file" : `${fileCount} files`;
    return `Ready — ${label} with your brief (recommended).`;
  }
  return SMART_CAMPAIGN_REQUIREMENT_HINT;
}

const VOICEOVER_HINT_PATTERN = /\b(?:voice\s*over|voiceover)\b|\b(?:narrat(?:ion|e|ing)|narrated)\b/i;
const VOICEOVER_NEGATIVE_PATTERN = /\b(?:no|without)\s+(?:voice\s*over|voiceover|narration)\b/i;
const PERSONALIZED_HINT_PATTERN = /\bpersonaliz(?:ed|ation)?\b|\btalking head\b|\bactor video\b|\bpicture[- ]in[- ]picture\b|\bpip\b/i;

/**
 * Lightweight hint from the user's brief — final voiceover/PiP decisions are made by the planner.
 * @param {string} prompt
 */
export function resolveSmartCampaignEnrichmentHints(prompt = "") {
  const text = String(prompt || "");
  const wantsVoiceover = VOICEOVER_HINT_PATTERN.test(text) && !VOICEOVER_NEGATIVE_PATTERN.test(text);
  return {
    wantsVoiceover,
    wantsPersonalized: PERSONALIZED_HINT_PATTERN.test(text),
  };
}

/**
 * @param {string} prompt
 * @param {number} elapsedSeconds
 */
export function getSmartCampaignLoadingSubmessage(prompt = "", elapsedSeconds = 0) {
  const { wantsVoiceover, wantsPersonalized } = resolveSmartCampaignEnrichmentHints(prompt);
  const elapsed = `Elapsed ${elapsedSeconds}s`;

  if (wantsVoiceover && wantsPersonalized) {
    return `Building the draft, then generating voiceover and personalized video. ${elapsed}`;
  }
  if (wantsVoiceover) {
    return `Building the draft, then generating voiceover audio. ${elapsed}`;
  }
  if (wantsPersonalized) {
    return `Building the draft, then generating personalized video. ${elapsed}`;
  }
  return `Building scenes and resolving media. ${elapsed}`;
}
